<script setup>
import DefaultModalBase from "../DefaultModalBase.vue";
import {useBulkSendStore} from "../../../store/bulkSends.js"
import {useModalStore} from "../../../store/modal.js"
import {useToastrStore} from "../../../store/toastr.js"
import {computed, onMounted, ref} from "vue";

const store = useBulkSendStore()
const modalStore = useModalStore()
const toastrStore = useToastrStore()

// Filters come straight from the orders sidebar, so the recipient list is the same
// list the export report produces.
const filters = computed(() => modalStore.props.filters ?? {})

const step = ref(1)
const loading = ref(false)
const error = ref('')
const is_sent = ref(false)
const template_id = ref(null)
const excluded = ref([])

const templateOptions = computed(() => store.templates.map(t => ({
    value: t.id,
    label: t.description || t.name,
})))

const selectedTemplate = computed(() => store.templates.find(t => t.id === template_id.value))

const recipients = computed(() => store.previewRecipients)
const counts = computed(() => store.previewCounts)

const sendableRecipients = computed(() => recipients.value.filter(r => r.sendable))

const isExcluded = (recipient) => excluded.value.includes(recipient.order_uuid)

const finalCount = computed(() => sendableRecipients.value.filter(r => !isExcluded(r)).length)

const allSelected = computed(() =>
    sendableRecipients.value.length > 0 && sendableRecipients.value.every(r => !isExcluded(r))
)

const toggleRecipient = (recipient) => {
    if (!recipient.sendable) return

    const index = excluded.value.indexOf(recipient.order_uuid)
    index === -1 ? excluded.value.push(recipient.order_uuid) : excluded.value.splice(index, 1)
}

const toggleAll = () => {
    excluded.value = allSelected.value ? sendableRecipients.value.map(r => r.order_uuid) : []
}

const heading = computed(() => {
    if (is_sent.value) return 'Bulk send started.'

    switch (step.value) {
        case 1:
            return 'Choose your message.'
        case 2:
            return `${finalCount.value} recipient(s) selected.`
        default:
            return 'Confirm bulk send.'
    }
})

const description = computed(() => {
    if (is_sent.value) return `Sending to ${finalCount.value} client(s). You'll get a notification when it's finished.`

    switch (step.value) {
        case 1:
            return 'Only approved WhatsApp templates can be sent in bulk.'
        case 2:
            return counts.value
                ? `${counts.value.total} client(s) matched your filters. Deselect anyone you don't want to message.`
                : ''
        default:
            return 'This cannot be undone once it starts.'
    }
})

const icon = computed(() => {
    if (is_sent.value) return 'check-all'

    return step.value === 1 ? 'speechbubble-double' : 'user-single-add'
})

const goToRecipients = async () => {
    loading.value = true
    error.value = ''

    try {
        await store.previewBulkSend({...filters.value, auto_reply_id: template_id.value})
        excluded.value = []
        step.value = 2
    } catch (e) {
        error.value = e.response?.data?.message || 'Could not build the recipient list.'
    } finally {
        loading.value = false
    }
}

const startSend = async () => {
    loading.value = true
    error.value = ''

    try {
        await store.createBulkSend({
            ...filters.value,
            auto_reply_id: template_id.value,
            excluded_order_uuids: excluded.value,
        })

        is_sent.value = true
        toastrStore.show(`Bulk send started for ${finalCount.value} client(s).`)
    } catch (e) {
        error.value = e.response?.data?.message || 'Could not start the bulk send.'
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    loading.value = true

    try {
        await store.getTemplates()
    } catch (e) {
        error.value = 'Could not load the message templates.'
    } finally {
        loading.value = false
    }
})
</script>

<template>
    <DefaultModalBase :description="description" :error="error" :heading="heading" :icon="icon" :loading="loading" :refresh="is_sent">

        <div class="bulk-send-container">

            <brand-button variant="secondary" v-if="is_sent" @click="modalStore.close(true)">Done</brand-button>

            <!-- Step 1: pick the template -->
            <template v-if="!is_sent && step === 1">
                <brand-form-select label="Message template." :options="templateOptions" v-model="template_id"/>
                <p class="bulk-send-template-preview" v-if="selectedTemplate?.content">“{{ selectedTemplate.content }}”</p>
                <brand-button @click="goToRecipients()" :disabled="!template_id">Next</brand-button>
            </template>

            <!-- Step 2: review and trim the recipient list -->
            <template v-if="!is_sent && step === 2">
                <div class="bulk-send-empty" v-if="!recipients.length">
                    <p>No clients match these filters.</p>
                </div>

                <template v-else>
                    <button class="bulk-send-select-all" @click.prevent="toggleAll()" v-if="sendableRecipients.length">
                        <ah-icon :name="allSelected ? 'checkbox-selected' : 'checkbox-deselected'" :size="18"/>
                        <span>{{ allSelected ? 'Deselect all' : 'Select all' }}</span>
                    </button>

                    <ul class="bulk-send-recipients">
                        <li :class="['bulk-send-recipient', {'--skipped': !r.sendable, '--excluded': isExcluded(r)}]"
                            v-for="r in recipients" :key="r.order_uuid">
                            <button class="bulk-send-recipient-toggle" :disabled="!r.sendable" @click.prevent="toggleRecipient(r)">
                                <ah-icon :name="r.sendable && !isExcluded(r) ? 'checkbox-selected' : 'checkbox-deselected'" :size="18"/>
                            </button>
                            <div class="bulk-send-recipient-detail">
                                <p class="bulk-send-recipient-name">{{ r.name }}</p>
                                <p class="bulk-send-recipient-meta">{{ r.phone_number }}</p>
                                <p class="bulk-send-recipient-skip" v-if="!r.sendable">{{ r.skip_reason_label }}</p>
                            </div>
                        </li>
                    </ul>

                    <p class="bulk-send-summary" v-if="counts">
                        {{ counts.sendable }} can be messaged · {{ counts.skipped }} skipped
                    </p>
                </template>

                <div class="bulk-send-actions">
                    <brand-button variant="secondary" @click="step = 1">Back</brand-button>
                    <brand-button @click="step = 3" :disabled="!finalCount">Review {{ finalCount }}</brand-button>
                </div>
            </template>

            <!-- Step 3: confirm -->
            <template v-if="!is_sent && step === 3">
                <ul class="bulk-send-confirm">
                    <li><span>Template</span><strong>{{ selectedTemplate?.description || selectedTemplate?.name }}</strong></li>
                    <li><span>Recipients</span><strong>{{ finalCount }}</strong></li>
                    <li v-if="excluded.length"><span>Removed</span><strong>{{ excluded.length }}</strong></li>
                </ul>

                <div class="bulk-send-actions">
                    <brand-button variant="secondary" @click="step = 2">Back</brand-button>
                    <brand-button @click="startSend()" :disabled="!finalCount">Send to {{ finalCount }}</brand-button>
                </div>
            </template>
        </div>
    </DefaultModalBase>
</template>

<style scoped lang="scss">
.bulk-send-container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
}

.bulk-send-template-preview {
    @include font(1.4, 2, book);
    font-style: italic;
    color: var(--color-grey-hazy);
}

.bulk-send-select-all {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    @include font(1.3, 1.3, black);
    color: var(--color-tiffany-dark);

    .icon {
        fill: var(--color-tiffany-dark);
    }
}

.bulk-send-recipients {
    list-style: none;
    max-height: 32rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: .5rem;
}

.bulk-send-recipient {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 1rem;
    border-radius: 1.2rem;
    background: var(--color-dew);

    &.--excluded {
        opacity: .5;
    }

    &.--skipped {
        opacity: .5;

        .bulk-send-recipient-toggle {
            cursor: not-allowed;
        }
    }

    .bulk-send-recipient-toggle {
        background: transparent;
        border: none;
        outline: none;
        cursor: pointer;
        display: flex;

        .icon {
            fill: var(--color-tiffany-dark);
        }
    }

    .bulk-send-recipient-name {
        @include font(1.4, 1.8, black);
    }

    .bulk-send-recipient-meta {
        @include font(1.2, 1.6, book);
        color: var(--color-grey-hazy);
    }

    .bulk-send-recipient-skip {
        @include font(1.2, 1.6, book);
        color: var(--color-secondary-orange);
    }

    @include dark() {
        background: var(--color-white-20);

        .bulk-send-recipient-name {
            color: var(--color-white);
        }
    }
}

.bulk-send-summary,
.bulk-send-empty p {
    @include font(1.3, 1.8, book);
    color: var(--color-grey-hazy);
}

.bulk-send-confirm {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 1rem;

    li {
        display: flex;
        justify-content: space-between;
        gap: 2rem;
        @include font(1.4, 2, book);

        strong {
            @include font(1.4, 2, black);
        }
    }

    @include dark() {
        li, li strong {
            color: var(--color-white);
        }
    }
}

.bulk-send-actions {
    display: flex;
    gap: 1rem;
}
</style>
